/**
 * "Pick your colors."
 *
 * Deliberately plain about what it is: five choices, all of them available,
 * none of them earned. A child taps one and the app changes immediately —
 * no confirm button, no saving spinner, no "are you sure".
 *
 * The immediacy is the point. Autonomy is the feeling of having done
 * something, and a choice that takes three taps and a confirmation does not
 * feel like one.
 */
import { THEMES } from '../themes'
import { TraceRegion } from '../lib/trace'
import type { ThemeId } from '../themes'
import { radius, theme, TAP_MIN } from '../theme'

export function ThemePicker({
  value, onPick, compact = false,
}: {
  value: string
  onPick: (id: ThemeId) => void
  /** Smaller, for the settings screen where it sits among other rows. */
  compact?: boolean
}) {
  const size = compact ? 52 : 68

  return (
    <TraceRegion id="T20" writes note="saved as a setting row: scope learner, key theme">
    <div
      role="radiogroup"
      aria-label="Color theme"
      style={{ display: 'flex', gap: compact ? 8 : 12, flexWrap: 'wrap' }}
    >
      {THEMES.map((t) => {
        const on = t.id === value
        return (
          <button
            key={t.id}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={t.label}
            onClick={() => onPick(t.id)}
            style={{
              fontFamily: 'inherit',
              cursor: 'pointer',
              background: t.palette.card,
              border: `3px solid ${on ? t.palette.orange : theme.border}`,
              borderRadius: radius.lg,
              padding: compact ? '8px 10px' : '12px 14px',
              minHeight: TAP_MIN,
              minWidth: size,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 6,
              // A gentle lift on the chosen one. No animation library: a
              // transform and a shadow say "this one" clearly enough.
              transform: on ? 'translateY(-2px)' : 'none',
              boxShadow: on
                ? `0 4px 14px ${t.palette.orange}44`
                : '0 1px 3px rgba(21,32,43,0.08)',
              transition: 'transform 120ms ease, box-shadow 120ms ease',
            }}
          >
            <span style={{ fontSize: compact ? 18 : 22, lineHeight: 1 }}>
              {t.emoji}
            </span>

            {/* Three dots: the child's colour, the growing colour, the paper. */}
            <span style={{ display: 'flex', gap: 3 }}>
              {t.swatch.map((c, i) => (
                <span
                  key={i}
                  style={{
                    width: compact ? 9 : 12,
                    height: compact ? 9 : 12,
                    borderRadius: '50%',
                    background: c,
                    border: `1px solid ${t.palette.border}`,
                  }}
                />
              ))}
            </span>

            <span style={{
              fontSize: compact ? 11 : 13,
              fontWeight: on ? 800 : 600,
              color: t.palette.text2,
            }}>
              {t.label}
            </span>
          </button>
        )
      })}
    </div>
    </TraceRegion>
  )
}
